"use client";

import React from "react";
import { PortableText } from "@portabletext/react";
import {
  customRichTextComponent,
  richTextComponent,
} from "@/sanity/sanityPortableText";
import Header from "@/components/global/partials/Header";
import Footer from "@/components/global/partials/Footer";
import { PageLegalSchema } from "@/sanity/pages/PageLegalTemplate";

import logo from "@/public/global/logo.svg";
import Image from "next/image";
import SimpleFooter from "../global/partials/SimpleFooter";

const NUMBER_PREFIX_RE = /^(\d+(?:\.\d+)*\.?)\s*/;

function HighlightedHeading({ children }: { children: React.ReactNode }) {
  const text = React.Children.toArray(children)
    .map((c) => (typeof c === "string" ? c : ""))
    .join("");

  const match = text.match(NUMBER_PREFIX_RE);
  if (!match) return <>{children}</>;

  const prefix = match[0];
  const rest = text.slice(prefix.length);
  return (
    <>
      <span className="text-[#98D8FF] font-medium tracking-[-0.1rem] font-spacemono">
        {match[1]}
      </span>
      {prefix.length > match[1].length ? " " : ""}
      {rest}
    </>
  );
}

export default function PageLegalJSX({ data }: { data: PageLegalSchema }) {
  const { title, blocksText } = data;

  return (
    <>
      <section className="relative w-full lg:px-0 px-[2rem] overflow-clip  bg-black">
        {data?.settings?.header && (
          <Header
            data={data.settings.header}
            headerStyle="light"
            className="lg:top-[1rem]"
            showBorder
          />
        )}

        <div className="relative z-[5] py-[4rem] lg:pb-[18.25rem] flex flex-col items-center">
          <a href="/" className="mb-[7.25rem] lg:mb-[12.5rem]">
            <Image
              src={logo}
              alt="logo"
              className="!w-[23rem] lg:!w-[31.42575rem]"
            />
          </a>

          {title && (
            <h1 className="font-[400] leading-[100%] font-news text-[5rem] lg:text-[9rem] text-white mb-[4rem]">
              {title}
            </h1>
          )}

          {blocksText?.length > 0 && (
            <div className=" w-[min(123rem,100%)] mx-auto bg-gradient-to-b from-[#0B0F13] from-[11.57%] to-[#98D8FF] to-[719.35%] rounded-[1.5rem] border border-white/10">
              <div className="flex flex-col gap-[2.5rem] p-[3rem] lg:px-[9rem] lg:py-[7rem] ">
                {blocksText.map((block, index) => (
                  <React.Fragment key={index}>
                    <div className="">
                      <PortableText
                        value={block.text ?? {}}
                        components={customRichTextComponent({
                          block: {
                            h2: ({ children }) => {
                              return (
                                <h2 className="text-[3rem] lg:text-[3.5rem] text-white font-medium tracking-normal leading-[120%] lg:leading-[150%] pr-[2rem] lg:pr-0 mb-[3rem] font-news">
                                  <HighlightedHeading>
                                    {children}
                                  </HighlightedHeading>
                                </h2>
                              );
                            },
                            h3: ({ children }) => {
                              return (
                                <h3 className="text-[2.5rem] lg:text-[3rem] text-white font-medium leading-[120%] lg:leading-[150%] pr-[2rem] lg:pr-0 mb-[3rem] font-news">
                                  <HighlightedHeading>
                                    {children}
                                  </HighlightedHeading>
                                </h3>
                              );
                            },
                            normal: ({ children }) => {
                              return (
                                <p className="text-white/60 leading-[150%] text-[2.25rem] lg:text-[2.5rem]">
                                  {children}
                                </p>
                              );
                            },
                          },
                        })}
                      />
                    </div>

                    {index < blocksText.length - 1 && (
                      <div className="px-[3rem]">
                        <hr className="w-full border-t h-[.125rem] border-t-black/20" />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>

        {data.settings.footer && (
          <SimpleFooter data={data.settings.footer} footerType={"lp"} />
        )}
      </section>
    </>
  );
}
